import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Star, 
  Filter,
  MessageCircle,
  Phone,
  Video,
  Clock,
  Award,
  Languages,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPECIALIZATIONS = [
  'All', 'Vedic Astrology', 'Kundali Matching', 'Career', 'Love & Marriage', 
  'Remedies', 'Numerology', 'Vastu', 'Palmistry'
];

export default function AstrologersPage({ user }) {
  const navigate = useNavigate();
  const [astrologers, setAstrologers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpec, setSelectedSpec] = useState('All');
  const [sortBy, setSortBy] = useState('rating');

  useEffect(() => {
    fetchAstrologers();
  }, []);

  const fetchAstrologers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/astrologers`);
      setAstrologers(response.data);
    } catch (error) {
      // Demo data
      setAstrologers([
        {
          id: '1',
          name: 'Pt. Ramesh Sharma',
          avatar: 'https://api.dicebear.com/7.x/personas/svg?seed=Ramesh',
          specialization: ['Vedic Astrology', 'Career'],
          experience: 20,
          rating: 4.9,
          reviews: 1250,
          price_per_min: 15,
          languages: ['Hindi', 'English'],
          is_online: true,
          total_consultations: 5000,
          bio: 'Expert in Vedic astrology with 20+ years of experience. Specialized in career guidance and remedies.'
        },
        {
          id: '2',
          name: 'Acharya Priya Devi',
          avatar: 'https://api.dicebear.com/7.x/personas/svg?seed=Priya',
          specialization: ['Kundali Matching', 'Love & Marriage'],
          experience: 15,
          rating: 4.8,
          reviews: 890,
          price_per_min: 12,
          languages: ['Hindi', 'English', 'Sanskrit'],
          is_online: true,
          total_consultations: 3500,
          bio: 'Specialist in kundali matching and relationship astrology. Helped thousands find their perfect match.'
        },
        {
          id: '3',
          name: 'Jyotishi Suresh Kumar',
          avatar: 'https://api.dicebear.com/7.x/personas/svg?seed=Suresh',
          specialization: ['Remedies', 'Vastu'],
          experience: 12,
          rating: 4.7,
          reviews: 650,
          price_per_min: 10,
          languages: ['Hindi'],
          is_online: false,
          total_consultations: 2800,
          bio: 'Expert in astrological remedies and Vastu Shastra. Provides practical solutions for life problems.'
        },
        {
          id: '4',
          name: 'Dr. Kavita Mishra',
          avatar: 'https://api.dicebear.com/7.x/personas/svg?seed=Kavita',
          specialization: ['Numerology', 'Career'],
          experience: 8,
          rating: 4.6,
          reviews: 420,
          price_per_min: 8,
          languages: ['Hindi', 'English'],
          is_online: true,
          total_consultations: 1500,
          bio: 'Numerologist and career astrologer. Combines numbers and stars for accurate predictions.'
        },
        {
          id: '5',
          name: 'Pandit Hari Om',
          avatar: 'https://api.dicebear.com/7.x/personas/svg?seed=HariOm',
          specialization: ['Vedic Astrology', 'Palmistry'],
          experience: 25,
          rating: 4.9,
          reviews: 2100,
          price_per_min: 20,
          languages: ['Hindi', 'Sanskrit'],
          is_online: false,
          total_consultations: 8000,
          bio: 'Senior Vedic astrologer with expertise in palmistry. Lineage of astrologers for 3 generations.'
        },
        {
          id: '6',
          name: 'Acharya Meena Sharma',
          avatar: 'https://api.dicebear.com/7.x/personas/svg?seed=Meena',
          specialization: ['Love & Marriage', 'Remedies'],
          experience: 10,
          rating: 4.5,
          reviews: 380,
          price_per_min: 9,
          languages: ['Hindi', 'English', 'Gujarati'],
          is_online: true,
          total_consultations: 1200,
          bio: 'Relationship astrologer specializing in love, marriage, and family matters.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAstrologers = astrologers
    .filter(a => {
      const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSpec = selectedSpec === 'All' || a.specialization.includes(selectedSpec);
      return matchesSearch && matchesSpec;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'price_low') return a.price_per_min - b.price_per_min;
      if (sortBy === 'price_high') return b.price_per_min - a.price_per_min;
      if (sortBy === 'experience') return b.experience - a.experience;
      return 0;
    });

  const startChat = (astrologerId) => {
    navigate(`/jyotish/consultation/${astrologerId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-4 px-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate('/jyotish')} className="p-2 hover:bg-white/10 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Talk to Astrologer</h1>
              <p className="text-purple-200 text-sm">ज्योतिषी से बात करें</p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search astrologers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300"
              data-testid="search-astrologers"
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Filters */}
        <div className="mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {SPECIALIZATIONS.map((spec) => (
              <button
                key={spec}
                onClick={() => setSelectedSpec(spec)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedSpec === spec
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-purple-100'
                }`}
              >
                {spec}
              </button>
            ))}
          </div>
        </div>

        {/* Sort */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            {filteredAstrologers.length} Astrologers available
          </p>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="rating">Top Rated</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="experience">Most Experienced</option>
          </select>
        </div>

        {/* Astrologers List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAstrologers.map((astrologer) => (
              <div
                key={astrologer.id}
                className="bg-white rounded-2xl p-4 shadow-md hover:shadow-lg transition-shadow"
                data-testid={`astrologer-card-${astrologer.id}`}
              >
                <div className="flex gap-4">
                  {/* Avatar */}
                  <div className="relative">
                    <img
                      src={astrologer.avatar}
                      alt={astrologer.name}
                      className="w-20 h-20 rounded-xl object-cover border-2 border-purple-200"
                    />
                    {astrologer.is_online && (
                      <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-gray-800">{astrologer.name}</h3>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {astrologer.specialization.slice(0, 2).map((spec) => (
                            <span key={spec} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                              {spec}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-600">₹{astrologer.price_per_min}</p>
                        <p className="text-xs text-gray-500">/min</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        {astrologer.rating} ({astrologer.reviews})
                      </span>
                      <span className="flex items-center gap-1">
                        <Award className="w-4 h-4 text-purple-500" />
                        {astrologer.experience} yrs
                      </span>
                      <span className="flex items-center gap-1">
                        <Languages className="w-4 h-4 text-blue-500" />
                        {astrologer.languages.join(', ')}
                      </span>
                    </div>

                    {/* Bio */}
                    <p className="text-sm text-gray-500 mt-2 line-clamp-2">{astrologer.bio}</p>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => startChat(astrologer.id)}
                        disabled={!astrologer.is_online}
                        className={`flex-1 py-2 rounded-xl font-medium text-sm flex items-center justify-center gap-2 ${
                          astrologer.is_online
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-md'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        data-testid={`chat-btn-${astrologer.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                        Chat
                      </button>
                      <button
                        disabled
                        className="flex-1 py-2 rounded-xl font-medium text-sm flex items-center justify-center gap-2 bg-gray-100 text-gray-400 cursor-not-allowed"
                      >
                        <Phone className="w-4 h-4" />
                        Call
                      </button>
                      <button
                        disabled
                        className="flex-1 py-2 rounded-xl font-medium text-sm flex items-center justify-center gap-2 bg-gray-100 text-gray-400 cursor-not-allowed"
                      >
                        <Video className="w-4 h-4" />
                        Video
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Become Astrologer CTA */}
        <div className="mt-8 bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-gray-800 text-lg mb-2">Are you an Astrologer?</h3>
          <p className="text-gray-600 text-sm mb-4">
            Join DharmJyotish and connect with thousands of seekers
          </p>
          <button
            onClick={() => navigate('/jyotish/register-astrologer')}
            className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium rounded-full hover:shadow-lg transition-shadow"
          >
            Register as Astrologer
          </button>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
